import { NAVIGATION_CONFIG } from '@/config/constants/navigation'
import { SETTINGS_CONFIG } from '@/config/constants/settings'

import { NavItemBase } from 'types/navigation'

type NavItem<Type extends NavItemBase> = Type extends
  | { path: string }
  | { actions: object }
  ? Type
  : Type extends {
        items: NavItemBase[]
      }
    ? NavItem<Type['items'][number]>
    : { id: never }

type OmitNever<Type> = {
  [Key in keyof Type as Type[Key] extends never ? never : Key]: Type[Key]
}

type WithoutNullableKeys<Type> = {
  [Key in keyof Type]-?: NonNullable<Type[Key]>
}

type RouteBase<T extends NavItemBase> = {
  [NavItemPointer in NavItem<T> as NavItemPointer['id']]: NavItemPointer extends {
    actions: object
  }
    ? OmitNever<WithoutNullableKeys<NavItemPointer['actions']>>
    : NavItemPointer extends { path: string }
      ? () => NavItemPointer['path']
      : never
}

type StandardRoutes = RouteBase<
  (typeof NAVIGATION_CONFIG)[number]['items'][number]
>
type SettingsRoutes = RouteBase<(typeof SETTINGS_CONFIG)[number]>

type Routes = StandardRoutes & SettingsRoutes

const getRouteConfig = (): Routes => {
  const mapItem = (item: NavItemBase) => {
    if (item.items) {
      return [...item.items.flatMap(mapItem)]
    }

    if (item.path && !item.actions) {
      return [[item.id, () => item.path]]
    }

    if (item.path && item.actions) {
      return [[item.id, item.actions]]
    }

    throw new Error('Invalid navigation config detected')
  }

  const navigation = Object.fromEntries(
    NAVIGATION_CONFIG.flatMap((group) => group.items as NavItemBase[]).flatMap(
      mapItem
    )
  )

  const settings = Object.fromEntries(SETTINGS_CONFIG.flatMap(mapItem))

  return {
    ...navigation,
    ...settings,
  } as Routes
}

/**
 * You can use this with the `useRouter` hook from `next/router` or any routing framework to programmatically navigate to a BuildPass route where ever you need it:
 *
 * ```
 * import { useRouter } from 'next/router'
 * import { routes } from '@/utils/index
 *
 * const MyComponent => () => {
 *   const router = useRouter()
 *
 *   const submitHandler = () => {
 *     // e.g the add new project screen
 *     router.push(routes.projects.new())
 *
 *     // the site diaries list with the given filters
 *     router.push(routes.siteDiaries.list({
 *       filter: {},
 *       sort: {},
 *       pagination: {},
 *     }))
 *
 *     // view subcontractor screen
 *     router.push(routes.subcontractors.view('subcontractorId'))
 *
 *     // open the document tab on the worker view screen
 *     router.push(routes.workers.view('workerId', {
 *       tab: 'documents',
 *     }))
 *   }
 *
 *   return <Button onClick={submitHandler}>Submit</Button>
 * }
 * ```
 * @returns Automatically generates and returns a type-friendly route interface based off of the navigation configuration stored at `src/constants/navigation.ts` and `src/constants/settings.ts`.
 */
export const routes = getRouteConfig()
